import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import {
  AlertCircle,
  Loader2,
  Bot,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import AgentIcon from '@/components/AgentIcon';
// import { AlertCircle, Loader2, Bot, BarChart3 } from 'lucide-react';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { useUIStore } from '@/stores';
import MobileHeader from '@/components/MobileHeader';
import { useHashNavigate } from '@/contexts/HashRouterContext';
// import { fetchWithAuth } from '@/lib/utils';
import Graph from '@/components/MessageBubble/Graph';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};
interface Agent {
  id: string;
  name: string;
  logo?: string;
  description?: string;
  enabled?: boolean;
  isActive?: boolean;
  lastCheckAt?: string;
  isDefault?: boolean;
}
// interface AgentsResponse {
//   clients: Agent[];
// }
export default function AgentsList() {
  const { t } = useTranslation();
  useForceUpdate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null);
  const [showGraph, setShowGraph] = useState(false);
  const { setCurrentConversationId } = useConversationsContext();
  const {
    agents,
    // agentsLoaded,
    setAgents,
    setAgentsLoaded,
    selectedAgent,
    setSelectedAgent,
    setNewChatAgent,
    fetchAgents,
  } = useUIStore();
  const navigate = useHashNavigate();
  useEffect(() => {
    // Force refresh agents data when component mounts
    setIsLoading(true);
    // Reset agentsLoaded to false to bypass the cache check in fetchAgents
    setAgentsLoaded(false);
    fetchAgents().finally(() => {
      setIsLoading(false);
    });
  }, [fetchAgents, setAgentsLoaded]);
  const handleAgentClick = async (agent: Agent) => {
    try {
      setLoadingAgentId(agent.id);
      // Fetch agent config
      // const response = await fetchWithAuth(`/api/clients/${agent.id}/config`);
      // if (!response.ok) {
      //   throw new Error(`Failed to fetch agent config: ${response.status}`);
      // }
      // const data = await response.json();
      // Save the complete agent info with config array
      // API returns { id, name, config: [...] }, we only need the config array
      const agentData = {
        ...agent,
        // config: data.config || [],
        config: [],
      };
      setSelectedAgent(agentData);
      setNewChatAgent(agentData);

      // Clear current conversation ID to ensure we start a new chat
      setCurrentConversationId(undefined);

      // Navigate to chat view
      navigate({ type: 'chat' });
    } catch (err) {
      console.error('Failed to fetch agent config:', err);
      // Even if config fetch fails, still navigate with basic agent info
      setSelectedAgent(agent);
      setNewChatAgent(agent);
      setCurrentConversationId(undefined);
      navigate({ type: 'chat' });
    } finally {
      setLoadingAgentId(null);
    }
  };
  const handleRetry = async () => {
    setAgents([]);
    setAgentsLoaded(false);
    setIsLoading(true);
    setError(null);
    try {
      await fetchAgents();
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAgents = (agents as unknown as Agent[]).filter(
    agent => agent.enabled
  );

  return (
    <div className="flex flex-col h-full bg-claude-bg">
      {/* Mobile Header */}
      <MobileHeader>
        <h3 className="text-base font-bold">AI Agents</h3>
      </MobileHeader>
      {/* Desktop Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 pr-3 pl-9 py-2 hidden lg:flex items-center justify-betwee">
        <h3 className="text-base font-bold">
          AI Agents
          {/* {t('agents.title')} */}
        </h3>
        {/* <button
          onClick={() => setShowGraph(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          title="View Graph"
        >
          <BarChart3 size={18} />
          <span className="font-medium">View Graph</span>
        </button> */}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
              <span className="text-gray-600">{t('agents.loadingAgents')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {t('agents.errorLoading')}
              </h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('login.tryAgain')}
              </button>
            </div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {t('agents.noAgentsFound')}
              </h2>
              <p className="text-gray-600">{t('agents.noAgentsAvailable')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredAgents.map(agent => (
              <div
                key={agent.id}
                onClick={() => handleAgentClick(agent)}
                className={`group bg-white rounded-xl border p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 relative ${
                  selectedAgent?.id === agent.id
                    ? 'border-blue-500 ring-1 ring-blue-500 shadow-md'
                    : 'border-gray-200 hover:border-blue-300'
                } ${loadingAgentId === agent.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {/* Loading Overlay */}
                {loadingAgentId === agent.id && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl z-10">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <AgentIcon
                    agent={agent}
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0 shadow-sm border border-gray-100"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                        {agent.name}
                      </h3>
                      {agent.isDefault && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-yellow-50 text-yellow-600 rounded-full border border-yellow-200">
                          {t('agents.default')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      {agent.isActive ? (
                        <ShieldCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <ShieldAlert className="w-4 h-4 text-red-500" />
                      )}

                      {agent.lastCheckAt && (
                        <span className="text-gray-400 font-medium">
                          • {new Date(agent.lastCheckAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {agent.description && (
                  <div className="mt-3 pt-3 border-t border-gray-50">
                    <p
                      className="text-sm text-gray-500 line-clamp-2 leading-relaxed"
                      title={agent.description}
                    >
                      {agent.description}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Graph Modal */}
      {showGraph && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative w-full h-full max-w-7xl max-h-[90vh] m-4">
            <button
              onClick={() => setShowGraph(false)}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <Graph />
          </div>
        </div>
      )}
    </div>
  );
}
